(() => {
  'use strict';

  const SELECTOR = '[data-frk-roof-size-selector]';
  const COLLECTION_PATH = (window.GRPRoofing && window.GRPRoofing.collectionPath) || '/collections/roofing-kits';
  const INDEX_VIEW = 'frk-kit-index';
  const SECTION_ID = 'main-collection-product-grid';
  const FALLBACK_MAX_PAGES = 8;
  const FALLBACK_PAGE_BATCH_SIZE = 4;
  const CACHE_PREFIX = 'frk-kit-family-v7-resilient-index:';
  const CONFIG_STEP_STORAGE_KEY = 'frk-configurator-open-step';
  const SIZE_SHEET_BODY_CLASS = 'frk-size-sheet-open';

  const normalise = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

  const numberValue = (value) => {
    const match = String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : Number.NaN;
  };

  const weightValue = (value) => {
    const text = String(value || '').replace(',', '.').toLowerCase();
    const multiplication = text.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    if (multiplication) return Number(multiplication[1]) * Number(multiplication[2]);

    const values = Array.from(text.matchAll(/\d+(?:\.\d+)?/g), (match) => Number(match[0]));
    return values.length ? Math.max(...values) : Number.NaN;
  };

  // Temporary range identity until custom.roof_kit_family is populated.
  // This keeps Trade Grade Standard / Anthracite / Premium Fire Rated (and
  // Stronghold colour ranges) separate when changing size or system strength.
  // Once the metaobject exists, familyKey takes precedence automatically.
  function inferredRangeLabel(item) {
    const explicit = String(item.familyLabel || '').trim();
    if (explicit) return explicit;

    const brand = normalise(item.brand);
    const titleHandle = normalise(`${item.title || ''} ${item.handle || ''}`);
    const sku = String(item.sku || '').trim().toUpperCase();
    const colour = String(item.colour || '').trim();
    const colourKey = normalise(colour);

    if (brand === 'trade grade') {
      if (sku.startsWith('TRP') || /premium\s*fire|fire\s*rated|\btrp\b/.test(titleHandle)) return 'premium-fire-rated';
      if (sku.startsWith('TRA') || titleHandle.includes('anthracite') || colourKey.includes('anthracite') || /\btra\b/.test(titleHandle)) return 'anthracite';
      return `standard:${colourKey || 'default'}`;
    }

    if (brand === 'stronghold') return `colour:${colourKey || 'default'}`;
    return colourKey ? `colour:${colourKey}` : 'default';
  }

  function rangeKey(item) {
    const explicit = normalise(item.familyKey);
    if (explicit) return `family:${explicit}`;
    return `${normalise(item.brand)}|${normalise(inferredRangeLabel(item))}`;
  }

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  function cacheKey(config) {
    const currentRange = rangeKey({
      brand: config.brand,
      familyKey: config.familyKey,
      familyLabel: config.familyLabel,
      colour: config.colour,
      title: config.currentTitle,
      handle: config.currentHandle,
      sku: config.currentSku
    });
    return `${CACHE_PREFIX}${normalise(config.brand)}|${normalise(config.colour)}|${currentRange}`;
  }

  function readCache(config) {
    try {
      const raw = sessionStorage.getItem(cacheKey(config));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  function writeCache(config, items) {
    try {
      sessionStorage.setItem(cacheKey(config), JSON.stringify(items));
    } catch (_) {
      // Storage may be blocked; both selectors still work without caching.
    }
  }

  function buildCollectionUrl(config) {
    const url = new URL(COLLECTION_PATH, window.location.origin);
    url.searchParams.set('view', INDEX_VIEW);
    if (config.brand) url.searchParams.append('filter.p.m.custom.brand', config.brand);
    if (config.colour) url.searchParams.append('filter.p.m.custom.topcoat_color', config.colour);
    return url.toString();
  }

  async function fetchIndex(url) {
    const response = await fetch(url, {
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error(`Kit selector request failed (${response.status})`);
    const payload = JSON.parse(await response.text());
    return Array.isArray(payload?.items) ? payload.items : [];
  }

  function buildFallbackCollectionUrl(config, page = 1, sectionOnly = true) {
    const url = new URL(COLLECTION_PATH, window.location.origin);
    if (config.brand) url.searchParams.append('filter.p.m.custom.brand', config.brand);
    if (config.colour) url.searchParams.append('filter.p.m.custom.topcoat_color', config.colour);
    if (page > 1) url.searchParams.set('page', String(page));
    if (sectionOnly) url.searchParams.set('section_id', SECTION_ID);
    return url.toString();
  }

  async function fetchFallbackHtml(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Kit selector fallback failed (${response.status})`);
    return response.text();
  }

  function parseFallbackPage(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const container = doc.querySelector('[data-product-container]');
    const totalPages = Math.min(
      FALLBACK_MAX_PAGES,
      Math.max(1, Number(container?.dataset.totalPages || 1))
    );
    const items = Array.from(doc.querySelectorAll('[data-frk-kit-index]')).map((node) => ({
      size: node.dataset.frkKitSize || '',
      brand: node.dataset.frkKitBrand || '',
      sku: node.dataset.frkKitSku || '',
      familyKey: node.dataset.frkKitFamilyKey || '',
      familyLabel: node.dataset.frkKitFamilyLabel || '',
      weight: node.dataset.frkKitWeight || '',
      colour: node.dataset.frkKitColour || '',
      url: node.dataset.frkKitUrl || '',
      handle: node.dataset.frkKitHandle || '',
      title: node.dataset.frkKitTitle || '',
      price: Number(node.dataset.frkKitPrice || 0),
      formattedPrice: node.dataset.frkKitPriceFormatted || '',
      available: node.dataset.frkKitAvailable !== 'false'
    }));
    return { totalPages, items };
  }

  async function fetchFallbackPage(config, page) {
    try {
      return parseFallbackPage(await fetchFallbackHtml(buildFallbackCollectionUrl(config, page, true)));
    } catch (_) {
      return parseFallbackPage(await fetchFallbackHtml(buildFallbackCollectionUrl(config, page, false)));
    }
  }

  async function fetchFallbackFamily(config) {
    const first = await fetchFallbackPage(config, 1);
    let allItems = first.items;

    for (let start = 2; start <= first.totalPages; start += FALLBACK_PAGE_BATCH_SIZE) {
      const end = Math.min(first.totalPages, start + FALLBACK_PAGE_BATCH_SIZE - 1);
      const requests = [];
      for (let page = start; page <= end; page += 1) {
        requests.push(fetchFallbackPage(config, page).then((result) => result.items).catch(() => []));
      }
      allItems = allItems.concat(...await Promise.all(requests));
    }

    return allItems;
  }

  function embeddedFamily(selector) {
    const node = selector.querySelector('[data-frk-kit-family-json]');
    if (!node?.textContent.trim()) return null;
    try {
      const items = JSON.parse(node.textContent);
      return Array.isArray(items) && items.length ? items : null;
    } catch (_) {
      return null;
    }
  }

  function exactFamily(items, config) {
    const brand = normalise(config.brand);
    const colour = normalise(config.colour);
    const currentRange = rangeKey({
      brand: config.brand,
      familyKey: config.familyKey,
      familyLabel: config.familyLabel,
      colour: config.colour,
      title: config.currentTitle,
      handle: config.currentHandle,
      sku: config.currentSku
    });

    return items.filter((item) => {
      // Include every published matching PDP, even if Shopify currently marks it
      // unavailable. These controls change product specification, not stock status.
      if (!item.url || Number.isNaN(numberValue(item.size)) || !item.weight) return false;
      if (brand && normalise(item.brand) !== brand) return false;
      if (colour && normalise(item.colour) !== colour) return false;

      // Critical like-for-like rule: never silently move a customer from TRP to
      // standard Trade Grade, from TRA to standard Trade Grade, or between a
      // Stronghold colour range when they only changed size/strength.
      if (currentRange && rangeKey(item) !== currentRange) return false;
      return true;
    });
  }

  function uniqueFamily(items, config) {
    const byProduct = new Map();

    items.forEach((item) => {
      const numericSize = numberValue(item.size);
      const numericWeight = weightValue(item.weight);
      if (Number.isNaN(numericSize) || Number.isNaN(numericWeight)) return;

      const key = item.handle || `${item.url}|${numericSize}|${numericWeight}`;
      const existing = byProduct.get(key);
      if (!existing || item.handle === config.currentHandle) {
        byProduct.set(key, { ...item, numericSize, numericWeight });
      }
    });

    if (![...byProduct.values()].some((item) => item.handle === config.currentHandle)) {
      byProduct.set(`current:${config.currentHandle}`, {
        size: config.currentSize,
        numericSize: numberValue(config.currentSize),
        brand: config.brand,
        familyKey: config.familyKey,
        familyLabel: config.familyLabel,
        weight: config.weight,
        numericWeight: weightValue(config.weight),
        colour: config.colour,
        url: config.currentUrl,
        handle: config.currentHandle,
        sku: config.currentSku,
        title: config.currentTitle,
        price: config.currentPrice,
        formattedPrice: config.currentPriceFormatted,
        available: true
      });
    }

    return Array.from(byProduct.values());
  }

  function productsForCurrentWeight(family, config) {
    const currentWeight = weightValue(config.weight);
    const bySize = new Map();

    family.forEach((item) => {
      if (item.numericWeight !== currentWeight) return;
      const key = String(item.numericSize);
      const existing = bySize.get(key);
      if (!existing || item.handle === config.currentHandle) bySize.set(key, item);
    });

    return Array.from(bySize.values()).sort((a, b) => a.numericSize - b.numericSize);
  }

  function productsForCurrentSize(family, config) {
    const currentSize = numberValue(config.currentSize);
    const byStrength = new Map();

    family.forEach((item) => {
      if (item.numericSize !== currentSize) return;
      const key = String(item.numericWeight);
      const existing = byStrength.get(key);
      if (!existing || item.handle === config.currentHandle) byStrength.set(key, item);
    });

    return Array.from(byStrength.values()).sort((a, b) => {
      const aMeta = strengthMeta(a);
      const bMeta = strengthMeta(b);
      return aMeta.rank - bMeta.rank || a.numericWeight - b.numericWeight;
    });
  }

  async function fetchFamily(config) {
    let compactError = null;
    try {
      const items = await fetchIndex(buildCollectionUrl(config));
      const family = uniqueFamily(exactFamily(items, config), config);
      if (family.length > 1) return family;
      compactError = new Error('Compact kit index returned no alternatives');
    } catch (error) {
      compactError = error;
    }

    // Safety net for stores where a custom collection view is blocked or stale.
    // It scans only the already-filtered brand/colour result (normally 1–4
    // pages), never the entire Roofing Kits collection.
    const fallbackItems = await fetchFallbackFamily(config);
    const fallbackFamily = uniqueFamily(exactFamily(fallbackItems, config), config);
    if (fallbackFamily.length > 1) return fallbackFamily;
    throw compactError || new Error('No matching roof-kit family was found');
  }

  async function resolveFamily(selector, config) {
    const directFamily = embeddedFamily(selector);
    if (directFamily) {
      const family = uniqueFamily(exactFamily(directFamily, config), config);
      if (family.length > 1) return family;
    }

    const cached = readCache(config);
    if (cached) {
      const family = uniqueFamily(exactFamily(cached, config), config);
      if (family.length > 1) return family;
    }

    const family = await fetchFamily(config);
    writeCache(config, family);
    return family;
  }

  function isCurrent(item, config) {
    return item.handle === config.currentHandle
      || normalise(item.url) === normalise(config.currentUrl);
  }

  function sizeChipMarkup(item, config, showDifference = false) {
    const active = isCurrent(item, config)
      || item.numericSize === numberValue(config.currentSize);
    const difference = priceDifference(item, config, active);
    const priceLabel = active ? 'Current' : showDifference ? difference.text : '';
    const priceClass = active ? ' is-current' : showDifference ? difference.className : '';

    return `
      <a class="frk-roof-size-selector__chip${active ? ' is-active' : ''}"
        href="${escapeHtml(item.url)}"
        ${active ? 'aria-current="page"' : ''}
        data-frk-size-chip
        data-frk-prefetch-url="${escapeHtml(item.url)}"
        data-frk-size="${escapeHtml(item.size)}"
        aria-label="${escapeHtml(item.size)} square metre roofing kit${active ? ', current selection' : priceLabel ? `, ${escapeHtml(priceLabel)}` : ''}">
        <span class="frk-roof-size-selector__chip-size"><strong>${escapeHtml(item.size)}</strong><small>m²</small></span>
        <span class="frk-roof-size-selector__chip-price${priceClass}"${priceLabel ? '' : ' aria-hidden="true"'}>${priceLabel ? escapeHtml(priceLabel) : '&nbsp;'}</span>
      </a>`;
  }

  function sizeSheetOptionMarkup(item, config) {
    const active = isCurrent(item, config)
      || item.numericSize === numberValue(config.currentSize);
    const formattedPrice = formatVatMoney(item.price, config);

    return `
      <a class="frk-size-sheet__option${active ? ' is-active' : ''}"
        href="${escapeHtml(item.url)}"
        ${active ? 'aria-current="page"' : ''}
        data-frk-size-sheet-option
        data-frk-prefetch-url="${escapeHtml(item.url)}"
        data-frk-size="${escapeHtml(item.size)}"
        aria-label="${escapeHtml(item.size)} square metre roofing kit, ${escapeHtml(formattedPrice)}${active ? ', current selection' : ''}">
        <span class="frk-size-sheet__size"><strong>${escapeHtml(item.size)}</strong><span>m²</span></span>
        <small class="frk-size-sheet__price">${escapeHtml(formattedPrice)}</small>
        ${active ? '<em class="frk-size-sheet__current">Current</em>' : ''}
      </a>`;
  }

  function renderSizeSheet(selector, sizes, config) {
    const grid = selector.querySelector('[data-frk-size-sheet-grid]');
    if (!grid) return;
    grid.innerHTML = sizes.map((item) => sizeSheetOptionMarkup(item, config)).join('');

    const context = selector.querySelector('[data-frk-size-sheet-context]');
    if (context) context.textContent = `Prices shown ${vatPriceContext()} for the selected brand, colour and system`;
  }

  function renderSizePricing(selector, sizes, config) {
    const track = selector.querySelector('[data-frk-size-track]');
    if (!track) return;

    const currentIndex = sizes.findIndex((item) => isCurrent(item, config)
      || item.numericSize === numberValue(config.currentSize));

    track.innerHTML = sizes.map((item, index) => sizeChipMarkup(
      item,
      config,
      currentIndex >= 0 && Math.abs(index - currentIndex) === 1
    )).join('');

    renderSizeSheet(selector, sizes, config);
  }

  function storeNextOpenStep(step) {
    try {
      sessionStorage.setItem(CONFIG_STEP_STORAGE_KEY, step);
    } catch (_) {
      // Progressive accordion still works if storage is blocked.
    }
  }

  function takeNextOpenStep() {
    try {
      const step = sessionStorage.getItem(CONFIG_STEP_STORAGE_KEY);
      sessionStorage.removeItem(CONFIG_STEP_STORAGE_KEY);
      return step;
    } catch (_) {
      return '';
    }
  }

  function setOpenStep(selector, requestedStep, options = {}) {
    const steps = Array.from(selector.querySelectorAll('[data-frk-config-step]'));
    if (!steps.length) return;

    let opened = null;
    steps.forEach((step) => {
      const key = step.dataset.frkConfigStep;
      const expanded = Boolean(requestedStep && key === requestedStep);
      step.classList.toggle('is-expanded', expanded);
      step.classList.toggle('is-collapsed', !expanded);

      const toggle = step.querySelector('[data-frk-config-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        toggle.setAttribute('aria-label', `${expanded ? 'Collapse' : 'Expand'} ${key === 'size' ? 'roof size' : key === 'strength' ? 'system strength' : 'roof surface'} choices`);
      }
      if (expanded) opened = step;
    });

    if (opened?.dataset.frkConfigStep === 'size') {
      // The size rail is display:none while its accordion step is collapsed on
      // mobile. Re-centre it every time the step becomes visible so the active
      // size chip cannot be left off-screen after navigating between kit PDPs.
      window.requestAnimationFrame(() => {
        centreActive(selector, false);
        updateArrows(selector);
      });
    }

    if (opened && options.scroll && window.matchMedia('(max-width: 767px)').matches) {
      window.requestAnimationFrame(() => {
        opened.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }

  function bindAccordion(selector) {
    if (selector.__frkAccordionBound) return;
    selector.__frkAccordionBound = true;

    const restored = takeNextOpenStep();
    setOpenStep(selector, ['size', 'strength', 'substrate'].includes(restored) ? restored : 'size');

    selector.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-frk-config-toggle]');
      if (!toggle) return;

      event.preventDefault();
      const key = toggle.dataset.frkConfigToggle;
      const step = selector.querySelector(`[data-frk-config-step="${key}"]`);
      const currentlyExpanded = step?.classList.contains('is-expanded');
      setOpenStep(selector, currentlyExpanded ? '' : key, { scroll: !currentlyExpanded });
    });

    selector.addEventListener('frk:substrate-selected', (event) => {
      const detail = event.detail || {};
      const summary = selector.querySelector('[data-frk-config-summary="substrate"]');
      if (summary && detail.summary) summary.textContent = detail.summary;

      // OSB3 is the straightforward completed route, so collapse the full
      // configurator after an explicit selection. Keep advisory substrates open
      // so primer/contact guidance is never hidden from the customer.
      if (detail.userInitiated && detail.key === 'osb') {
        window.setTimeout(() => setOpenStep(selector, ''), 360);
      }
    });
  }

  function getSizeSheet(selector) {
    return selector.querySelector('[data-frk-size-sheet]');
  }

  function openSizeSheet(selector, opener) {
    const dialog = getSizeSheet(selector);
    if (!dialog) return;
    dialog.__frkLastOpener = opener || document.activeElement;

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    document.body.classList.add(SIZE_SHEET_BODY_CLASS);
    window.requestAnimationFrame(() => dialog.querySelector('[data-frk-size-sheet-close]')?.focus({ preventScroll: true }));
  }

  function closeSizeSheet(selector) {
    const dialog = getSizeSheet(selector);
    if (!dialog) return;

    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');

    document.body.classList.remove(SIZE_SHEET_BODY_CLASS);
    const opener = dialog.__frkLastOpener;
    dialog.__frkLastOpener = null;
    if (opener?.isConnected) window.requestAnimationFrame(() => opener.focus({ preventScroll: true }));
  }

  function bindSizeSheet(selector) {
    const dialog = getSizeSheet(selector);
    if (!dialog || selector.__frkSizeSheetBound) return;
    selector.__frkSizeSheetBound = true;

    selector.querySelectorAll('[data-frk-size-sheet-open]').forEach((opener) => {
      opener.addEventListener('click', () => openSizeSheet(selector, opener));
    });

    dialog.querySelectorAll('[data-frk-size-sheet-close]').forEach((closer) => {
      closer.addEventListener('click', () => closeSizeSheet(selector));
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeSizeSheet(selector);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeSizeSheet(selector);
    });

    dialog.addEventListener('close', () => document.body.classList.remove(SIZE_SHEET_BODY_CLASS));

    dialog.addEventListener('click', (event) => {
      const sizeLink = event.target.closest('[data-frk-size-sheet-option]');
      if (sizeLink) {
        if (sizeLink.classList.contains('is-active')) {
          event.preventDefault();
          closeSizeSheet(selector);
          return;
        }
        storeNextOpenStep('size');
        sizeLink.classList.add('is-navigating');
      }

      if (event.target.closest('[data-frk-config-help-open]')) {
        closeSizeSheet(selector);
      }
    });
  }

  function strengthMeta(item) {
    const raw = String(item.weight || '').trim();
    const lower = raw.toLowerCase();
    const numeric = Number.isFinite(item.numericWeight) ? item.numericWeight : weightValue(raw);
    const isBalcony = lower.includes('balcony')
      || /2\s*[x×]\s*450/.test(lower)
      || numeric >= 900;

    if (isBalcony) {
      return {
        label: 'Balcony',
        detail: '2 × 450g layers',
        rank: 3
      };
    }

    if (numeric >= 600) {
      return { label: 'Heavy Duty', detail: `${numeric}g glass`, rank: 2 };
    }

    return { label: 'Standard Duty', detail: `${numeric}g glass`, rank: 1 };
  }

  function formatMoney(cents, config) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: config.currency || 'GBP'
      }).format(amount);
    } catch (_) {
      return `£${amount.toFixed(2)}`;
    }
  }

  function getVatMode() {
    const apiMode = window.__VAT && typeof window.__VAT.getMode === 'function'
      ? window.__VAT.getMode()
      : '';
    const rootMode = document.documentElement.getAttribute('data-vat-mode') || '';
    const mode = apiMode || rootMode || 'ex';
    return mode === 'inc' || mode === 'both' ? mode : 'ex';
  }

  function priceCentsForVat(cents, mode = getVatMode()) {
    const exVatCents = Number(cents || 0);
    return mode === 'inc' || mode === 'both'
      ? Math.round(exVatCents * 1.2)
      : exVatCents;
  }

  function formatVatMoney(cents, config) {
    return formatMoney(priceCentsForVat(cents), config);
  }

  function vatPriceContext() {
    return getVatMode() === 'ex' ? 'ex. VAT' : 'inc. VAT';
  }

  function priceDifference(item, config, active) {
    if (active) return { text: 'Current', className: ' is-current' };

    // Compare the same VAT-adjusted amounts that are visibly shown on the cards.
    // This avoids a penny mismatch caused by rounding the difference separately.
    const candidate = priceCentsForVat(item.price);
    const current = priceCentsForVat(config.currentPrice);
    const difference = candidate - current;

    if (difference > 0) return { text: `+${formatMoney(difference, config)}`, className: ' is-increase' };
    if (difference < 0) return { text: `−${formatMoney(Math.abs(difference), config)}`, className: ' is-saving' };
    return { text: 'Same price', className: '' };
  }

  function strengthCardMarkup(item, config) {
    const active = isCurrent(item, config)
      || item.numericWeight === weightValue(config.weight);
    const meta = strengthMeta(item);
    const difference = priceDifference(item, config, active);
    const formattedPrice = formatVatMoney(item.price, config);

    return `
      <a class="frk-roof-size-selector__strength${active ? ' is-active' : ''}"
        href="${escapeHtml(item.url)}"
        ${active ? 'aria-current="page"' : ''}
        data-frk-strength-chip
        data-frk-prefetch-url="${escapeHtml(item.url)}"
        data-frk-strength="${escapeHtml(meta.label)}"
        aria-label="${escapeHtml(meta.label)}, ${escapeHtml(meta.detail)}, ${escapeHtml(formattedPrice)}${active ? ', current selection' : `, ${escapeHtml(difference.text)}`}">
        <span class="frk-roof-size-selector__strength-label">${escapeHtml(meta.label)}</span>
        <span class="frk-roof-size-selector__strength-detail">${escapeHtml(meta.detail)}</span>
        <strong class="frk-roof-size-selector__strength-price">${escapeHtml(formattedPrice)}</strong>
        <small class="frk-roof-size-selector__strength-difference${difference.className}">${escapeHtml(difference.text)}</small>
      </a>`;
  }

  function strengthDescription(label) {
    if (label === 'Balcony') {
      return 'Designed for balconies, terraces and roof areas subject to regular foot traffic.';
    }

    if (label === 'Heavy Duty') {
      return 'Additional reinforcement for a stronger, more impact-resistant roof system and more demanding applications.';
    }

    return 'Suitable for most domestic flat roofs where access is mainly for inspection and maintenance.';
  }

  function strengthHelpCardMarkup(item, config) {
    const active = isCurrent(item, config)
      || item.numericWeight === weightValue(config.weight);
    const meta = strengthMeta(item);
    const difference = priceDifference(item, config, active);
    const formattedPrice = formatVatMoney(item.price, config);

    return `
      <a class="frk-config-help__strength-card${active ? ' is-active' : ''}"
        href="${escapeHtml(item.url)}"
        ${active ? 'aria-current="page"' : ''}
        data-frk-config-strength-link
        data-frk-prefetch-url="${escapeHtml(item.url)}"
        aria-label="${escapeHtml(meta.label)}, ${escapeHtml(meta.detail)}, ${escapeHtml(formattedPrice)}${active ? ', current selection' : `, ${escapeHtml(difference.text)}`}">
        <span class="frk-config-help__strength-icon" aria-hidden="true">${active ? '✓' : meta.rank}</span>
        <span class="frk-config-help__strength-copy">
          <span class="frk-config-help__strength-title-row">
            <strong>${escapeHtml(meta.label)}</strong>
            ${active ? '<small>Current</small>' : ''}
          </span>
          <span class="frk-config-help__strength-spec">${escapeHtml(meta.detail)}</span>
          <span class="frk-config-help__strength-description">${escapeHtml(strengthDescription(meta.label))}</span>
          <span class="frk-config-help__strength-price-row">
            <strong>${escapeHtml(formattedPrice)}</strong>
            <small class="${difference.className.trim()}">${escapeHtml(difference.text)}</small>
          </span>
        </span>
        <svg class="frk-config-help__strength-chevron" viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
          <path d="m7.5 4.5 5.5 5.5-5.5 5.5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>`;
  }

  function getHelpRoot(selector) {
    if (selector.__frkConfigHelpRoot) return selector.__frkConfigHelpRoot;
    const trigger = selector.querySelector('[data-frk-config-help-open][aria-controls]');
    return trigger ? document.getElementById(trigger.getAttribute('aria-controls')) : null;
  }

  function renderStrengthHelp(selector, strengths, config) {
    const root = getHelpRoot(selector);
    const options = root?.querySelector('[data-frk-config-help-strength-options]');
    if (!options) return;

    options.innerHTML = strengths.length
      ? strengths.map((item) => strengthHelpCardMarkup(item, config)).join('')
      : '<p class="frk-config-help__loading">No alternative system strengths are currently available for this roof size.</p>';
  }


  function renderStrengthPricing(selector, strengths, config) {
    const strengthGrid = selector.querySelector('[data-frk-strength-grid]');
    if (!strengthGrid) return;

    strengthGrid.innerHTML = strengths.map((item) => strengthCardMarkup(item, config)).join('');
    strengthGrid.style.setProperty('--frk-strength-count', String(Math.min(3, Math.max(1, strengths.length))));
    renderStrengthHelp(selector, strengths, config);

    const priceContext = selector.querySelector('[data-frk-strength-price-context]');
    if (priceContext) {
      priceContext.textContent = `Prices shown ${vatPriceContext()} for ${config.currentSize}m²`;
    }
  }

  function openConfigHelp(selector, type, trigger) {
    const root = getHelpRoot(selector);
    if (!root) return;

    const title = root.querySelector('[data-frk-config-help-title]');
    const panel = root.querySelector('.frk-config-help__panel');
    const contentPanels = root.querySelectorAll('[data-frk-config-help-panel]');
    const selectedPanel = root.querySelector(`[data-frk-config-help-panel="${type}"]`);
    if (!panel || !selectedPanel) return;

    contentPanels.forEach((content) => {
      content.hidden = content !== selectedPanel;
    });

    if (title) {
      title.textContent = type === 'strength'
        ? 'Which system strength do I need?'
        : 'Correct GRP coverage rates';
    }

    window.clearTimeout(root.__frkCloseTimer);
    selector.__frkConfigHelpTrigger = trigger || null;
    root.hidden = false;
    root.dataset.frkConfigHelpActive = type;
    document.body.classList.add('frk-config-help-open');

    window.requestAnimationFrame(() => {
      root.classList.add('is-open');
      const closeButton = root.querySelector('.frk-config-help__close-icon');
      (closeButton || panel).focus({ preventScroll: true });
    });
  }

  function closeConfigHelp(selector) {
    const root = getHelpRoot(selector);
    if (!root || root.hidden) return;

    root.classList.remove('is-open');
    document.body.classList.remove('frk-config-help-open');

    const finish = () => {
      root.hidden = true;
      root.removeAttribute('data-frk-config-help-active');
      const trigger = selector.__frkConfigHelpTrigger;
      selector.__frkConfigHelpTrigger = null;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    } else {
      root.__frkCloseTimer = window.setTimeout(finish, 220);
    }
  }

  function trapHelpFocus(event, root) {
    if (event.key !== 'Tab') return;

    const focusable = Array.from(root.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);

    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function bindHelpInteractions(selector) {
    let root = selector.querySelector('[data-frk-config-help]');
    if (!root || selector.__frkConfigHelpBound) return;

    selector.__frkConfigHelpBound = true;
    selector.__frkConfigHelpRoot = root;

    // Moving the fixed drawer to <body> prevents product-column overflow or
    // transformed ancestors from clipping the overlay in some theme layouts.
    if (root.parentElement !== document.body) document.body.appendChild(root);

    selector.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-frk-config-help-open]');
      if (!trigger) return;
      event.preventDefault();
      openConfigHelp(selector, trigger.dataset.frkConfigHelpOpen, trigger);
    });

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-frk-config-help-close]')) {
        event.preventDefault();
        closeConfigHelp(selector);
        return;
      }

      const strengthLink = event.target.closest('[data-frk-config-strength-link]');
      if (strengthLink?.classList.contains('is-active')) {
        event.preventDefault();
        return;
      }
      if (strengthLink) {
        storeNextOpenStep('strength');
        strengthLink.classList.add('is-navigating');
      }
    });

    const prefetchFromHelp = (event) => {
      const link = event.target.closest('[data-frk-prefetch-url]');
      if (link && !link.classList.contains('is-active')) prefetchPage(link.href);
    };

    root.addEventListener('mouseover', prefetchFromHelp, { passive: true });
    root.addEventListener('focusin', prefetchFromHelp);

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeConfigHelp(selector);
        return;
      }
      trapHelpFocus(event, root);
    });
  }

  function updateArrows(selector) {
    const viewport = selector.querySelector('[data-frk-size-viewport]');
    const prev = selector.querySelector('[data-frk-size-prev]');
    const next = selector.querySelector('[data-frk-size-next]');
    if (!viewport || !prev || !next) return;

    const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const overflow = max > 4;
    const atStart = viewport.scrollLeft <= 4;
    const atEnd = viewport.scrollLeft >= max - 4;

    prev.hidden = !overflow;
    next.hidden = !overflow;
    prev.classList.toggle('is-disabled', atStart);
    next.classList.toggle('is-disabled', atEnd);
  }

  function centreActive(selector, smooth = false) {
    const viewport = selector.querySelector('[data-frk-size-viewport]');
    const active = selector.querySelector('[data-frk-size-chip].is-active');
    if (!viewport || !active) return;

    const left = active.offsetLeft - ((viewport.clientWidth - active.offsetWidth) / 2);
    viewport.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
  }

  function prefetchPage(url) {
    if (!url || url === window.location.pathname || url === window.location.href) return;
    const absolute = new URL(url, window.location.origin).href;

    // On fine-pointer desktop devices, the PDP soft-navigation controller
    // warms exactly the hovered/focused product and reuses that same response
    // for the content swap. Mobile retains the existing lightweight behaviour.
    if (window.FRKPDPSoftNavigation?.prefetch?.(absolute)) return;

    const exists = Array.from(document.querySelectorAll('link[data-frk-kit-prefetch]'))
      .some((link) => link.href === absolute);
    if (exists) return;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = absolute;
    link.dataset.frkKitPrefetch = 'true';
    document.head.appendChild(link);
  }

  function bindInteractions(selector) {
    const viewport = selector.querySelector('[data-frk-size-viewport]');
    const prev = selector.querySelector('[data-frk-size-prev]');
    const next = selector.querySelector('[data-frk-size-next]');
    if (!viewport || !prev || !next || selector.__frkKitSelectorBound) return;

    selector.__frkKitSelectorBound = true;
    bindAccordion(selector);

    // bindHelpInteractions (the strength/coverage help drawer, moved to
    // <body>) and bindSizeSheet (the mobile size bottom-sheet) are both
    // dialog-only UI, not part of the always-visible selector. Both are
    // idempotent (they guard on their own __frkConfigHelpBound/
    // __frkSizeSheetBound flags), so binding them at idle time and, as a
    // safety net, synchronously on the first click of their own open
    // triggers are both safe - whichever happens first wins, and the other
    // becomes a no-op. The capture-phase listener below runs before the
    // real bubble-phase handler it attaches, so that same first click still
    // opens the dialog with no second click required.
    const deferredHelpAndSizeSheetBind = () => {
      bindHelpInteractions(selector);
      bindSizeSheet(selector);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(deferredHelpAndSizeSheetBind, { timeout: 2000 });
    } else {
      window.setTimeout(deferredHelpAndSizeSheetBind, 200);
    }

    selector.addEventListener('click', (event) => {
      if (!selector.__frkConfigHelpBound && event.target.closest('[data-frk-config-help-open]')) {
        bindHelpInteractions(selector);
      }
      if (!selector.__frkSizeSheetBound && event.target.closest('[data-frk-size-sheet-open]')) {
        bindSizeSheet(selector);
      }
    }, true);

    const scrollAmount = () => Math.max(160, viewport.clientWidth * .78);
    prev.addEventListener('click', () => viewport.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }));
    next.addEventListener('click', () => viewport.scrollBy({ left: scrollAmount(), behavior: 'smooth' }));
    viewport.addEventListener('scroll', () => updateArrows(selector), { passive: true });

    selector.addEventListener('click', (event) => {
      const sizeLink = event.target.closest('[data-frk-size-chip]');
      const strengthLink = event.target.closest('[data-frk-strength-chip]');
      const link = sizeLink || strengthLink;
      if (!link) return;
      if (link.classList.contains('is-active')) {
        event.preventDefault();
        return;
      }

      link.classList.add('is-navigating');
      if (sizeLink) {
        storeNextOpenStep('size');
        const status = selector.querySelector('[data-frk-size-status]');
        if (status) status.textContent = `Opening ${sizeLink.dataset.frkSize}m² kit…`;
      } else {
        storeNextOpenStep('strength');
        const status = selector.querySelector('[data-frk-strength-status]');
        if (status) status.textContent = `Opening ${strengthLink.dataset.frkStrength} kit…`;
      }
    });

    const prefetchFromEvent = (event) => {
      const link = event.target.closest('[data-frk-prefetch-url]');
      if (link && !link.classList.contains('is-active')) prefetchPage(link.href);
    };

    selector.addEventListener('mouseover', prefetchFromEvent, { passive: true });
    selector.addEventListener('focusin', prefetchFromEvent);

    window.addEventListener('resize', () => {
      updateArrows(selector);
      centreActive(selector, false);
    }, { passive: true });
  }

  async function initialise(selector) {
    if (selector.dataset.frkSizeReady === 'true') return;
    selector.dataset.frkSizeReady = 'true';
    selector.classList.add('is-loading');
    bindInteractions(selector);

    const config = {
      currentSize: selector.dataset.frkCurrentSize || '',
      currentUrl: selector.dataset.frkCurrentUrl || window.location.pathname,
      currentHandle: selector.dataset.frkCurrentHandle || '',
      currentSku: selector.dataset.frkCurrentSku || '',
      currentTitle: selector.dataset.frkCurrentTitle || '',
      currentPrice: Number(selector.dataset.frkCurrentPrice || 0),
      currentPriceFormatted: selector.dataset.frkCurrentPriceFormatted || '',
      currency: selector.dataset.frkCurrency || 'GBP',
      brand: selector.dataset.frkBrand || '',
      familyKey: selector.dataset.frkFamilyKey || '',
      familyLabel: selector.dataset.frkFamilyLabel || '',
      weight: selector.dataset.frkWeight || '',
      colour: selector.dataset.frkColour || ''
    };

    const sizeTrack = selector.querySelector('[data-frk-size-track]');
    const sizeStatus = selector.querySelector('[data-frk-size-status]');
    const strengthGrid = selector.querySelector('[data-frk-strength-grid]');
    const strengthStatus = selector.querySelector('[data-frk-strength-status]');
    if (!sizeTrack || !sizeStatus || !strengthGrid || !strengthStatus) return;

    try {
      const family = await resolveFamily(selector, config);
      const sizes = productsForCurrentWeight(family, config);
      const strengths = productsForCurrentSize(family, config);

      renderSizePricing(selector, sizes, config);
      selector.__frkConfig = config;
      selector.__frkSizes = sizes;
      selector.__frkStrengths = strengths;
      renderStrengthPricing(selector, strengths, config);

      const sizeSummary = selector.querySelector('[data-frk-config-summary="size"]');
      if (sizeSummary) sizeSummary.textContent = `${config.currentSize}m² selected`;

      sizeStatus.textContent = sizes.length > 1
        ? `${sizes.length} roof sizes · ${config.currentSize}m² selected`
        : `${config.currentSize}m² selected`;

      const activeStrength = strengths.find((item) => isCurrent(item, config)) || strengths.find((item) => item.numericWeight === weightValue(config.weight));
      const activeMeta = activeStrength ? strengthMeta(activeStrength) : { label: config.weight, detail: '' };
      const strengthSummary = selector.querySelector('[data-frk-config-summary="strength"]');
      if (strengthSummary) {
        const activePrice = activeStrength ? formatVatMoney(activeStrength.price, config) : '';
        const details = [activeMeta.label, activeMeta.detail, activePrice].filter(Boolean);
        strengthSummary.textContent = details.join(' · ');
      }
      strengthStatus.textContent = strengths.length > 1
        ? `${strengths.length} strengths · ${activeMeta.label} selected`
        : `${activeMeta.label} selected`;
    } catch (error) {
      console.warn('[FRK kit selectors]', error);
      sizeStatus.textContent = 'Other roof sizes are temporarily unavailable';
      strengthStatus.textContent = 'Other system strengths are temporarily unavailable';
      selector.classList.add('has-data-error');
    } finally {
      selector.classList.remove('is-loading');
      window.requestAnimationFrame(() => {
        centreActive(selector, false);
        updateArrows(selector);
      });
    }
  }

  function initAll(root = document) {
    root.querySelectorAll(SELECTOR).forEach(initialise);
  }

  function refreshVatPricing(root = document) {
    root.querySelectorAll(SELECTOR).forEach((selector) => {
      const config = selector.__frkConfig;
      if (!config) return;

      if (Array.isArray(selector.__frkSizes)) {
        renderSizePricing(selector, selector.__frkSizes, config);
      }

      if (Array.isArray(selector.__frkStrengths)) {
        renderStrengthPricing(selector, selector.__frkStrengths, config);
        const activeStrength = selector.__frkStrengths.find((item) => isCurrent(item, config))
          || selector.__frkStrengths.find((item) => item.numericWeight === weightValue(config.weight));
        const activeMeta = activeStrength ? strengthMeta(activeStrength) : { label: config.weight, detail: '' };
        const summary = selector.querySelector('[data-frk-config-summary="strength"]');
        if (summary) {
          const activePrice = activeStrength ? formatVatMoney(activeStrength.price, config) : '';
          summary.textContent = [activeMeta.label, activeMeta.detail, activePrice].filter(Boolean).join(' · ');
        }
      }
    });
  }

  window.FRKRoofKitSelector = Object.assign(window.FRKRoofKitSelector || {}, {
    initAll,
    refreshVatPricing,
    openStep(step, scope = document) {
      const selector = scope.matches?.(SELECTOR) ? scope : scope.querySelector?.(SELECTOR);
      if (!selector) return;
      bindInteractions(selector);
      setOpenStep(selector, ['size', 'strength', 'substrate'].includes(step) ? step : 'size');
      if (step === 'size') {
        window.requestAnimationFrame(() => {
          centreActive(selector, false);
          updateArrows(selector);
        });
      }
    }
  });

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('frk:pdp-soft-navigated', (event) => initAll(event.detail?.scope || document));
  window.addEventListener('vat:modeChanged', () => refreshVatPricing());
})();
